import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Headphones, Phone } from "@phosphor-icons/react";
import FlowDesigner from "@/components/FlowDesigner";
import TestCallModal from "@/components/TestCallModal";

const empty = () => ({
  number: "", description: "", status: "active",
  assignment_type: "unassigned", inbound_group_id: "__none__",
  agent_user_id: "__none__", external_sip_uri: "", external_sip_port: "5060",
  sip_carrier_id: "__none__", notes: "",
});

const userLabel = (u) => {
  const name = (u.name || u.email || "User").trim();
  const sip = u.phone?.sip_username;
  return sip ? `${name} · ${sip}` : name;
};

export default function CustomerDids() {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [designer, setDesigner] = useState(null);
  const [savingFollow, setSavingFollow] = useState(false);
  const [testCall, setTestCall] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, g, c, u] = await Promise.all([
        api.get("/customer-routing/dids", { params: q.trim() ? { q: q.trim() } : {} }),
        api.get("/customer-routing/inbound-groups"),
        api.get("/customer-routing/sip-carriers"),
        api.get("/customer-team/users").catch(() => ({ data: [] })),
      ]);
      setRows(d.data || []);
      setGroups(g.data || []);
      setCarriers(c.data || []);
      setUsers(u.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty()); setDlg("new"); };
  const openEdit = (r) => {
    setForm({
      number: r.number || "",
      description: r.description || "",
      status: r.status || "active",
      assignment_type: r.assignment_type || "unassigned",
      inbound_group_id: r.inbound_group_id || "__none__",
      agent_user_id: r.agent_user_id || "__none__",
      external_sip_uri: r.external_sip_uri || "",
      external_sip_port: String(r.external_sip_port || 5060),
      sip_carrier_id: r.sip_carrier_id || "__none__",
      notes: r.notes || "",
    });
    setDlg(r);
  };

  const save = async () => {
    if (!form.number.trim()) { toast.error("DID number is required"); return; }
    if (form.assignment_type === "inbound_group" && form.inbound_group_id === "__none__") {
      toast.error("Select an inbound group"); return;
    }
    if (form.assignment_type === "direct_agent" && form.agent_user_id === "__none__") {
      toast.error("Select a user"); return;
    }
    if (form.assignment_type === "external_sip") {
      if (!form.external_sip_uri.trim()) { toast.error("Enter the external SIP URL"); return; }
      const port = Number(form.external_sip_port);
      if (!Number.isInteger(port) || port < 1 || port > 65535) {
        toast.error("Enter a SIP port between 1 and 65535"); return;
      }
    }
    if (form.assignment_type === "call_follow") {
      const designed = dlg && dlg !== "new" && dlg.call_follow_designed;
      if (!designed) {
        toast.error("Design Call Follow on this DID first"); return;
      }
    }
    setSaving(true);
    try {
      const body = {
        number: form.number.trim(),
        description: form.description || null,
        status: form.status,
        assignment_type: form.assignment_type,
        inbound_group_id: form.assignment_type === "inbound_group" && form.inbound_group_id !== "__none__"
          ? form.inbound_group_id : null,
        agent_user_id: form.assignment_type === "direct_agent" && form.agent_user_id !== "__none__"
          ? form.agent_user_id : null,
        external_sip_uri: form.assignment_type === "external_sip" ? form.external_sip_uri.trim() : null,
        external_sip_port: form.assignment_type === "external_sip" ? Number(form.external_sip_port) : null,
        sip_carrier_id: form.sip_carrier_id !== "__none__" ? form.sip_carrier_id : null,
        notes: form.notes || null,
      };
      if (dlg === "new") {
        await api.post("/customer-routing/dids", body);
        toast.success("DID added");
      } else {
        await api.patch(`/customer-routing/dids/${dlg.id}`, body);
        toast.success("DID updated");
      }
      setDlg(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete DID ${r.number}?`)) return;
    try {
      await api.delete(`/customer-routing/dids/${r.id}`);
      toast.success("DID deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const assignLabel = (r) => {
    if (r.assignment_type === "inbound_group") {
      const g = groups.find((x) => x.id === r.inbound_group_id);
      return g ? `Group: ${g.name}` : "Inbound group";
    }
    if (r.assignment_type === "direct_agent") {
      const u = users.find((x) => x.id === r.agent_user_id);
      return u ? `Agent: ${u.name || u.email}` : `Agent: ${r.agent_destination || "—"}`;
    }
    if (r.assignment_type === "external_sip") {
      const uri = r.external_sip_uri || r.agent_destination || "—";
      return r.external_sip_port ? `External SIP: ${uri}:${r.external_sip_port}` : `External SIP: ${uri}`;
    }
    if (r.assignment_type === "call_follow") return "Call Follow";
    return "Unassigned";
  };

  const openTest = async (r) => {
    if (!r.call_follow_designed) return;
    try {
      const { data } = await api.get(`/customer-routing/dids/${r.id}`);
      setTestCall({
        id: data.id,
        name: data.description || data.number,
        flow: data.call_follow || {},
        did_id: data.id,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const openFollow = async (r) => {
    try {
      const { data } = await api.get(`/customer-routing/dids/${r.id}`);
      setDesigner({ did: data, flow: data.call_follow || { nodes: [], edges: [] } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const saveFollow = async (flow, opts = {}) => {
    if (!designer?.did?.id) return;
    setSavingFollow(true);
    try {
      await api.put(`/customer-routing/dids/${designer.did.id}/call-follow`, {
        flow, draft: !!opts.draft,
      });
      toast.success(opts.keepOpen ? "Draft saved — keep editing" : "Call Follow saved");
      if (opts.keepOpen) setDesigner((d) => (d ? { ...d, flow } : d));
      else { setDesigner(null); load(); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSavingFollow(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dids-page">
      <PageHeader
        title="DID Management"
        subtitle="Assign DIDs to inbound groups, a user’s softphone, External SIP, or a designed Call Follow"
      />
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-56" placeholder="Search DIDs…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button className="rounded-lg" onClick={openNew} data-testid="did-add">
          <Plus className="w-4 h-4 mr-1.5" /> Add DID
        </Button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : rows.length === 0 ? <EmptyState title="No DIDs" description="Register a DID and assign it to an inbound group or agent destination." />
          : (
            <div className="rounded-xl border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Number</TableHead>
                    <TableHead>Assignment</TableHead>
                    <TableHead>Carrier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-44" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <div className="font-mono font-medium">{r.number}</div>
                        {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{assignLabel(r)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {carriers.find((c) => c.id === r.sip_carrier_id)?.name || "—"}
                      </TableCell>
                      <TableCell><StatusPill status={r.status} /></TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2"
                          onClick={() => openFollow(r)}
                          data-testid={`did-call-follow-${r.id}`}
                          title={r.call_follow_designed ? "Edit Call Follow" : "Design Call Follow"}
                          aria-label="Call Follow"
                        >
                          <Headphones className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2"
                          disabled={!r.call_follow_designed}
                          onClick={() => openTest(r)}
                          data-testid={`did-test-call-${r.id}`}
                          title={r.call_follow_designed ? "Test call" : "Design Call Follow first"}
                          aria-label="Test call"
                        >
                          <Phone className="w-4 h-4" weight="fill" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`did-edit-${r.id}`}>
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(r)}>
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="did-dialog">
          <DialogHeader>
            <DialogTitle>{dlg === "new" ? "Add DID" : "Edit DID"}</DialogTitle>
            <DialogDescription>
              Assign to an inbound group, a user’s softphone, External SIP, or Call Follow (only after it is designed).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>DID number</Label>
              <Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="+15551234567" data-testid="did-number" /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Assignment</Label>
              <Select value={form.assignment_type} onValueChange={(v) => setForm({ ...form, assignment_type: v })}>
                <SelectTrigger data-testid="did-assignment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  <SelectItem value="inbound_group">Inbound group</SelectItem>
                  <SelectItem value="direct_agent">Direct to agent</SelectItem>
                  <SelectItem value="external_sip">External SIP</SelectItem>
                  <SelectItem
                    value="call_follow"
                    disabled={!(dlg && dlg !== "new" && dlg.call_follow_designed)}
                  >
                    Call Follow
                  </SelectItem>
                </SelectContent>
              </Select>
              {!(dlg && dlg !== "new" && dlg.call_follow_designed) && (
                <p className="text-xs text-muted-foreground">
                  Design Call Follow first (headphones icon) before you can assign it.
                </p>
              )}
            </div>
            {form.assignment_type === "inbound_group" && (
              <div className="space-y-1.5"><Label>Inbound group</Label>
                <Select value={form.inbound_group_id} onValueChange={(v) => setForm({ ...form, inbound_group_id: v })}>
                  <SelectTrigger data-testid="did-inbound-group"><SelectValue placeholder="Select group" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select…</SelectItem>
                    {groups.filter((g) => g.status === "active").map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.assignment_type === "direct_agent" && (
              <div className="space-y-1.5">
                <Label>User</Label>
                <Select value={form.agent_user_id} onValueChange={(v) => setForm({ ...form, agent_user_id: v })}>
                  <SelectTrigger data-testid="did-agent-user"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select…</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{userLabel(u)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The inbound call rings this user’s registered softphone.
                </p>
              </div>
            )}
            {form.assignment_type === "external_sip" && (
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_7rem] gap-3">
                <div className="space-y-1.5">
                  <Label>SIP URL</Label>
                  <Input
                    value={form.external_sip_uri}
                    onChange={(e) => setForm({ ...form, external_sip_uri: e.target.value })}
                    placeholder="user@pbx.example.com or sip:user@host"
                    data-testid="did-external-sip-uri"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>SIP port</Label>
                  <Input
                    type="number"
                    min={1}
                    max={65535}
                    value={form.external_sip_port}
                    onChange={(e) => setForm({ ...form, external_sip_port: e.target.value })}
                    data-testid="did-external-sip-port"
                  />
                </div>
                <p className="text-xs text-muted-foreground sm:col-span-2">
                  The inbound call is sent to this SIP address on the port you set.
                </p>
              </div>
            )}
            <div className="space-y-1.5"><Label>SIP carrier <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Select value={form.sip_carrier_id} onValueChange={(v) => setForm({ ...form, sip_carrier_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {carriers.filter((c) => c.status === "active").map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={save} disabled={saving} data-testid="did-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TestCallModal campaign={testCall} open={!!testCall} onClose={() => setTestCall(null)} />

      <Dialog open={!!designer} onOpenChange={(v) => !v && setDesigner(null)}>
        <DialogContent className="max-w-[95vw] w-[95vw]" data-testid="did-call-follow-dialog">
          <DialogHeader>
            <DialogTitle>
              Call Follow — {designer?.did?.number}
              {designer?.did?.description ? ` · ${designer.did.description}` : ""}
            </DialogTitle>
            <DialogDescription>
              Design the inbound follow for this DID. Assignment can use Call Follow only after this flow is ready.
            </DialogDescription>
          </DialogHeader>
          {designer && (
            <FlowDesigner initial={designer.flow} onSave={saveFollow} saving={savingFollow} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
